/**
 * 帳號程式關聯 API 服務
 */

import apiClient from '../axios.js';

/**
 * 帳號程式關聯服務
 */
export const accountProgramService = {
    /**
     * 獲取 API 基礎路徑
     * @param {string} accountType - 帳號類型：'system', 'platform', 'client' 或 'staff'
     * @returns {string} API 基礎路徑
     */
    getBasePath(accountType = 'platform') {
        if (accountType === 'system') {
            return '/system/accounts';
        }
        if (accountType === 'client') {
            return '/system/clients/accounts';
        }
        if (accountType === 'staff') {
            return '/client/staff/accounts';
        }
        return '/system/platforms/accounts';
    },

    /**
     * 查詢帳號的程式列表
     * @param {string} accountId - 帳號ID
     * @param {string} accountType - 帳號類型：'system' 或 'platform'，預設為 'platform'
     * @returns {Promise} API 回應
     */
    async getProgramsByAccountId(accountId, accountType = 'platform') {
        const basePath = this.getBasePath(accountType);
        const response = await apiClient.get(`${basePath}/${accountId}/programs`);
        return response;
    },

    /**
     * 查詢帳號可選的程式列表（根據角色對應的模組或方案對應的模組）
     * @param {string} accountId - 帳號ID
     * @param {string} roleCode - 角色代碼（系統/平台帳號需要，客戶帳號不需要）
     * @param {string} accountType - 帳號類型：'system', 'platform' 或 'client'，預設為 'platform'
     * @returns {Promise} API 回應
     */
    async getAvailablePrograms(accountId, roleCode = null, accountType = 'platform') {
        const basePath = this.getBasePath(accountType);
        const params = { accountType };
        if (roleCode) {
            params.roleCode = roleCode;
        }
        const response = await apiClient.get(`${basePath}/${accountId}/programs/available`, {
            params
        });
        return response;
    },

    /**
     * 查詢當前客戶可用的程式列表（用於新增帳號時）
     * @param {string} accountType - 帳號類型：'staff'
     * @returns {Promise} API 回應
     */
    async getAvailableProgramsForCreate(accountType = 'staff') {
        const basePath = this.getBasePath(accountType);
        const response = await apiClient.get(`${basePath}/available-programs`);
        return response;
    },

    /**
     * 批量添加程式到帳號
     * @param {string} accountId - 帳號ID
     * @param {Array<string>} programIds - 程式ID列表
     * @param {Object} programPermissions - 程式權限設定 { programId: [permission1, permission2] 或 null }
     * @param {string} accountType - 帳號類型：'system' 或 'platform'，預設為 'platform'
     * @returns {Promise} API 回應
     */
    async addPrograms(accountId, programIds, programPermissions = null, accountType = 'platform') {
        const basePath = this.getBasePath(accountType);
        const payload = { programIds };
        if (programPermissions) {
            payload.programPermissions = programPermissions;
        }
        const response = await apiClient.post(`${basePath}/${accountId}/programs`, payload);
        return response;
    },

    /**
     * 從帳號移除程式
     * @param {string} accountId - 帳號ID
     * @param {string} programId - 程式ID
     * @param {string} accountType - 帳號類型：'system' 或 'platform'，預設為 'platform'
     * @returns {Promise} API 回應
     */
    async removeProgram(accountId, programId, accountType = 'platform') {
        const basePath = this.getBasePath(accountType);
        const response = await apiClient.delete(`${basePath}/${accountId}/programs/${programId}`);
        return response;
    },

    /**
     * 更新帳號中程式的允許權限
     * @param {string} accountId - 帳號ID
     * @param {string} programId - 程式ID
     * @param {Array|null} allowedPermissions - 允許的權限陣列，null表示允許全部
     * @param {string} accountType - 帳號類型：'system' 或 'platform'，預設為 'platform'
     * @returns {Promise} API 回應
     */
    async updateProgramPermissions(accountId, programId, allowedPermissions, accountType = 'platform') {
        const basePath = this.getBasePath(accountType);
        const response = await apiClient.patch(
            `${basePath}/${accountId}/programs/${programId}/permissions`,
            { allowedPermissions }
        );
        return response;
    }
};
